import { CSSProperties, useEffect, useState } from 'react';
import { useHistory } from 'react-router-dom';
import dayjs from 'dayjs';
import millify from 'millify';
import { Area, AreaChart, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts';

// Components
import PageSymphony from 'elements/PageSymphony';
import CardSymphony from 'elements/CardSymphony';
import ButtonSymphony from 'elements/ButtonSymphony';
import Loader from 'elements/Loader';
import BrandInfo from 'elements/BrandInfo';
import TableElement, { ColDef } from 'elements/TableElement';
import TabsSymphony, { TabItem } from 'elements/TabsSymphony';
import TabSymphony from 'elements/TabSymphony';
import DashboardDateSelector from 'components/shareable/DashboardDateSelector';
import PlatformToggles, { PlatformToggle } from 'components/shareable/PlatformToggles/PlatformToggles';
import CampaignsSidebar, { adaptCampaignsForSidebar } from 'components/shareable/CampaignsSidebar/CampaignsSidebar';
import SocialConnectModal from 'components/shareable/SocialConnectModal';
import ModalSymphony from 'elements/ModalSymphony';
import PlatformsConnector from 'pages/DashboardPage/PlatformsConnector';
import AdConnectionModal from 'components/shareable/AdConnectionModal';
import AddBrandToOrganizationModalV2 from 'components/shareable/AddBrandToOrganizationModalV2/AddBrandToOrganizationModalV2';
import BrandActionMenu from 'pages/OrganizationPage/componentsOrganization/BrandActionMenu';
import DeleteBrandConfirmationModal from 'pages/OrganizationPage/componentsOrganization/DeleteBrandConfirmationModal';
import EditBrandUserPermissionsModal from 'pages/OrganizationPage/componentsOrganization/EditBrandUserPermissionsModal/EditBrandUserPermissionsModal';
import Icon from 'elements/Icon';

// Hooks
import useBrand from 'Hooks/useBrand';
import useOrganizationV2 from 'Hooks/useOrganizationV2';
import useScreen from 'Hooks/useScreen';
import { useDashboard } from 'Hooks/useDashboard';
import useAppView from 'Hooks/useAppView';
import { useCampaign } from 'Hooks/useCampaign';

// Utils & Constants
import Colors from 'modules/Colors';
import { OrganizationDashboardBrandData, OrganizationCampaignData, PlatformConnections } from 'api/organizationApi';
import ProfilePictureSection from 'components/shareable/ProfilePictureSection';
import { SocialConnectType, CampaignStatus, AdConnectionType, PlanType, OrganizationRole } from 'modules/Const';
import { CurrentBrand } from 'modules/Types';
import { DATE_OPTIONS } from 'pages/DashboardPage/utils';
import { FilterTypeDate, FilterTypeBudget } from 'pages/MarketingPage/Components/CampaignTypesFilter/CampaignFilterTypes';
import useGlobalModals from 'Hooks/useGlobalModals';
import useUser from 'Hooks/useUser';
import { useContext } from 'react';
import { UpgradeModalContext } from 'Hooks/UpgradeModalContext';

// Define proper types for the chart data
interface ChartDataPoint {
    date: string;
    value: number;
}


// Define the structure of fanbase data from the backend
interface FanbaseChartData {
    datetime: string;
    fanbase_count: number;
}

// Tab definitions
enum OrganizationHomeTabs {
    BRANDS = 'brands',
    GRAPHS = 'graphs',
}

const ORGANIZATION_HOME_TABS = [
    {
        id: OrganizationHomeTabs.BRANDS,
        title: 'Brands',
    },
    // {
    //     id: OrganizationHomeTabs.GRAPHS,
    //     title: 'Graphs',
    // },
];

const OrganizationHomePage = () => {
    const history = useHistory();
    const { mobileView, mStyle } = useScreen();
    const { organization, hasOrganizationAdminAccess, deleteOrganizationBrand, setSelectedBrand, getSelectedBrand } = useOrganizationV2();
    const { setCurrentBrand, loadedBrandsDict } = useBrand();
    const { currentUser } = useUser();
    const {
        getOrganizationDashboard,
        organizationDashboardData,
        isLoadingOrganizationDashboard,
        selectedDate,
        setSelectedDate,
        timeRange,
        setTimeRange
    } = useDashboard();
    const {
        openCreateCampaign,
        openCreateWebsite,
        openBrandSelector
    } = useGlobalModals();
    const { handleOpenModal: openUpgradeModal } = useContext(UpgradeModalContext);
    const { setAppView } = useAppView();

    // Use cached campaigns from useCampaign hook
    const {
        campaigns: organizationCampaigns,
        isLoadingCampaigns,
        isCampaignsInitialized
    } = useCampaign();

    const selectedBrand = getSelectedBrand() as CurrentBrand;
    // Tab state
    const [selectedTab, setSelectedTab] = useState<TabItem>(ORGANIZATION_HOME_TABS[0]);
    
    const [isFirstLoad, setIsFirstLoad] = useState(true);
    const [currentProfilePicture, setCurrentProfilePicture] = useState<string | undefined>(organization?.profile_picture);
    const [selectedPlatforms, setSelectedPlatforms] = useState<string[]>(['overall_fanbase']);
    const [showConnectionModal, setShowConnectionModal] = useState(false);
    const [connectionBrandId, setConnectionBrandId] = useState<number | null>(null);
    const [connectionPlatformType, setConnectionPlatformType] = useState<SocialConnectType | null>(null);
    const [showEditConnectionsModal, setShowEditConnectionsModal] = useState(false);
    const [showLargeGraph, setShowLargeGraph] = useState<{
        shown: boolean;
        brandData: OrganizationDashboardBrandData | null;
        platformKey?: string;
    }>({ shown: false, brandData: null });
    const [showAddBrandModal, setShowAddBrandModal] = useState(false);
    const [deleteBrandModalOpen, setDeleteBrandModalOpen] = useState(false);
    const [editPermissionsModalOpen, setEditPermissionsModalOpen] = useState(false);

    // Local platform connection state (separate from global dashboard state)
    const [selectedPlatformForConnect, setSelectedPlatformForConnect] = useState<SocialConnectType | null>(null);
    const [connectionBrandForModal, setConnectionBrandForModal] = useState<CurrentBrand | null>(null);

    // Define available platforms for toggling
    const availablePlatforms: PlatformToggle[] = [
        {
            id: 'overall',
            name: 'Overall',
            statIdentifier: 'fanbase',
            icon: 'fans',
            color: Colors.purple
        },
        {
            id: 'spotify',
            name: 'Spotify',
            statIdentifier: 'monthly_listeners',
            icon: 'spotify',
            color: '#1DB954'
        },
        {
            id: 'spotify',
            name: 'Spotify Followers',
            statIdentifier: 'followers',
            icon: 'spotify',
            color: '#1DB954'
        },
        {
            id: 'youtube',
            name: 'YouTube',
            statIdentifier: 'subscribers',
            icon: 'youtubeFlat',
            color: '#FF0000'
        },
        {
            id: 'instagram',
            name: 'Instagram',
            statIdentifier: 'followers',
            icon: 'instagram',
            color: '#E4405F'
        },
        {
            id: 'tiktok',
            name: 'TikTok',
            statIdentifier: 'followers',
            icon: 'tiktok',
            color: '#000000'
        },
        {
            id: 'facebook',
            name: 'Facebook',
            statIdentifier: 'followers',
            icon: 'facebook',
            color: '#1877F2'
        },
        {
            id: 'soundcloud',
            name: 'SoundCloud',
            statIdentifier: 'followers',
            icon: 'soundcloud',
            color: '#FF5500'
        },
        {
            id: 'audiomack',
            name: 'Audiomack',
            statIdentifier: 'followers',
            icon: 'audiomack',
            color: '#FF6600'
        }
    ];

    useEffect(() => {
        if (organization?.id && isFirstLoad) {
            loadDashboardData();
            setIsFirstLoad(false);
        }
    }, [organization]);

    useEffect(() => {
        if (organization?.id && !isFirstLoad) {
            loadDashboardData();
        }
    }, [selectedDate, timeRange, selectedPlatforms]);

    useEffect(() => {
        setCurrentProfilePicture(organization?.profile_picture);
    }, [organization?.profile_picture]);

    const loadDashboardData = async () => {
        if (!organization?.id) return;

        await getOrganizationDashboard({
            organizationId: organization.id,
            startDate: selectedDate.startDate,
            endDate: selectedDate.endDate,
            ...(selectedPlatforms.length > 0 && { platforms: selectedPlatforms })
        });
    };

    const handleViewBrand = (brandId: number) => {
        // Find the brand in the dashboard data
        const brand = organizationDashboardData?.find(b => b.brandId === brandId);
        if (brand) {
            // Create a brand object compatible with setCurrentBrand
            const brandForStore = {
                id: brand.brandId,
                name: brand.brandName,
                slug: brand.brandSlug,
                tier: brand.brandTier,
                // Add other required properties as needed
            };
            setCurrentBrand(brandForStore as any);
            setAppView('brand', history);
        }
    };

    const handleUpgradeBrand = (brandId: number) => {
        openUpgradeModal({
            source: 'organization_home_table'
        });
    };

    const handleDowngradeBrand = (brandId: number) => {
        // Similar to upgrade but for downgrade
        console.log('Downgrade brand:', brandId);
        // TODO: Implement downgrade modal
    };

    const renderBrandCard = (brandData: OrganizationDashboardBrandData) => {
        const fanbaseData = brandData.fanbaseData;
        const platformData: Record<string, any> = brandData.platformData || {};
        const platformConnections: PlatformConnections = brandData.platformConnections;
        const weeklyChartData = fanbaseData?.weeklyChartData as FanbaseChartData[] | undefined;
        const currentFanbase = weeklyChartData?.[weeklyChartData.length - 1]?.fanbase_count || 0;
        const growthPercentage = fanbaseData?.weeklyGrowth?.percentage ?? 0;

        // Transform data for the chart with proper typing
        const chartData: ChartDataPoint[] = weeklyChartData?.map((d: FanbaseChartData) => ({
            date: d.datetime,
            value: d.fanbase_count
        })) || [];

        // Prepare platform overlays
        const platformChartData: Record<string, ChartDataPoint[]> = {};
        const platformColors: Record<string, string> = {};

        selectedPlatforms.forEach(platformKey => {
            const platformStats = platformData[platformKey];
            if (platformStats && platformStats.chartData) {
                platformChartData[platformKey] = platformStats.chartData.map((d: any) => ({
                    date: d.datetime,
                    value: d.fanbase_count
                }));

                // Find the platform info for color
                const platform = availablePlatforms.find(p => `${p.id}_${p.statIdentifier}` === platformKey);
                platformColors[platformKey] = platform?.color || Colors.greyDark;
            }
        });

        // Check for missing connections
        const missingConnections = selectedPlatforms.filter(platformKey => {
            const [platformId] = platformKey.split('_');
            return !platformConnections[platformId as keyof PlatformConnections];
        });

        return (
            <CardSymphony
                key={brandData.brandId}
                className="brandCard"
                style={sty.brandCard}
                title={
                    <BrandInfo
                        titleColor={Colors.black}
                        brandName={brandData.brandName}
                        brandImage={brandData.brandImage || undefined}
                        brandSlug={brandData.brandSlug}
                    />
                }
            >
                <div className="brandCardContent" style={sty.brandCardContent}>
                    {brandData.error ? (
                        <div style={sty.errorMessage}>
                            Unable to load data for this brand
                        </div>
                    ) : fanbaseData ? (
                        <>
                            <div className="fanbaseStats" style={sty.fanbaseStats}>
                                <div className="statItem">
                                    <div style={sty.statLabel}>Total Fanbase</div>
                                    <div style={{ ...sty.statValue, color: Colors.black }}>{millify(currentFanbase)}</div>
                                </div>
                                <div className="statItem">
                                    <div style={sty.statLabel}>Growth</div>
                                    <div style={{
                                        ...sty.statValue,
                                        color: growthPercentage >= 0 ? Colors.green : Colors.red
                                    }}>
                                        {growthPercentage >= 0 ? '+' : ''}{growthPercentage?.toFixed(1) || '0.0'}%
                                    </div>
                                </div>
                            </div>

                            {/* {missingConnections.length > 0 && (
                                <div className="missingConnections" style={sty.missingConnections}>
                                    <div style={sty.missingConnectionsText}>
                                        Connect {missingConnections.map(platformKey => {
                                            const platform = availablePlatforms.find(p => `${p.id}_${p.statIdentifier}` === platformKey);
                                            return platform?.name;
                                        }).join(', ')} to track followers
                                    </div>
                                    <ButtonSymphony
                                        className="connectPlatformButton"
                                        variant="text"
                                        size="small"
                                        onClick={() => handleConnectPlatform(brandData.brandId, missingConnections)}
                                    >
                                        Connect
                                    </ButtonSymphony>
                                </div>
                            )} */}

                            {chartData.length > 0 && (
                                <div
                                    className="graphContainer"
                                    style={{ ...sty.graphContainer, cursor: 'pointer' }}
                                    onClick={() => handleExpandGraph(brandData)}
                                    title="Click to expand graph"
                                >
                                    <ResponsiveContainer width="100%" height={140}>
                                        <AreaChart
                                            data={chartData}
                                            margin={{ top: 5, right: 5, left: 5, bottom: 15 }}
                                        >
                                            <defs>
                                                <linearGradient id={`gradient-${brandData.brandId}`} x1="0" y1="0" x2="0" y2="1">
                                                    <stop offset="5%" stopColor={Colors.purple} stopOpacity={0.8} />
                                                    <stop offset="95%" stopColor={Colors.purple} stopOpacity={0.1} />
                                                </linearGradient>
                                                {/* Platform gradients */}
                                                {Object.entries(platformColors).map(([platformKey, color]) => (
                                                    <linearGradient key={platformKey} id={`gradient-${brandData.brandId}-${platformKey}`} x1="0" y1="0" x2="0" y2="1">
                                                        <stop offset="5%" stopColor={color} stopOpacity={0.6} />
                                                        <stop offset="95%" stopColor={color} stopOpacity={0.1} />
                                                    </linearGradient>
                                                ))}
                                            </defs>
                                            <XAxis
                                                dataKey="date"
                                                axisLine={false}
                                                tickLine={false}
                                                tick={{ fontSize: 10, fill: Colors.greyDark }}
                                                tickFormatter={(value) => dayjs(value).format('MMM DD')}
                                                interval="preserveStartEnd"
                                            />
                                            <YAxis
                                                axisLine={false}
                                                tickLine={false}
                                                tick={{ fontSize: 10, fill: Colors.greyDark }}
                                                tickFormatter={(value: number) => millify(value)}
                                                width={40}
                                            />
                                            <Tooltip
                                                contentStyle={{
                                                    backgroundColor: Colors.white,
                                                    border: `1px solid ${Colors.greyLight}`,
                                                    borderRadius: 8,
                                                    padding: '8px 12px',
                                                    boxShadow: '0 4px 12px rgba(0,0,0,0.1)'
                                                }}
                                                labelStyle={{ color: Colors.black, fontSize: 12, fontWeight: 600 }}
                                                itemStyle={{ color: Colors.black }}
                                                labelFormatter={(value) => dayjs(value).format('MMM DD, YYYY')}
                                                formatter={(value: number) => [millify(value), 'Fans']}
                                            />
                                            {/* Main fanbase area */}
                                            <Area
                                                type="monotone"
                                                dataKey="value"
                                                stroke={Colors.purple}
                                                strokeWidth={2}
                                                fillOpacity={1}
                                                fill={`url(#gradient-${brandData.brandId})`}
                                            />
                                            {/* Platform overlays */}
                                            {Object.entries(platformChartData).map(([platformKey, data]) => (
                                                <Area
                                                    key={platformKey}
                                                    type="monotone"
                                                    dataKey="value"
                                                    data={data}
                                                    stroke={platformColors[platformKey]}
                                                    strokeWidth={1.5}
                                                    fillOpacity={0.3}
                                                    fill={`url(#gradient-${brandData.brandId}-${platformKey})`}
                                                />
                                            ))}
                                        </AreaChart>
                                    </ResponsiveContainer>
                                </div>
                            )}
                        </>
                    ) : (
                        <div style={sty.loadingContainer}>
                            <Loader />
                            <p style={sty.loadingText}>Gathering fanbase data...</p>
                        </div>
                    )}
                </div>
            </CardSymphony>
        );
    };

    const renderEmptyState = () => {
        return (
            <div style={sty.emptyState}>
                <h2 style={sty.emptyStateTitle}>Welcome to Your Team Dashboard</h2>
                <p style={sty.emptyStateText}>
                    Add brands to your organization to see their performance metrics here.
                </p>
                <ButtonSymphony
                    className="addBrandButton"
                    variant="primary"
                    onClick={() => history.push('/add-artist')}
                >
                    Add Your First Brand
                </ButtonSymphony>
            </div>
        );
    };

    const renderBrandsTab = () => {
        if (isLoadingOrganizationDashboard) {
            return (
                <div style={sty.loadingContainer}>
                    <Loader />
                    <p style={sty.loadingText}>Loading organization dashboard...</p>
                </div>
            );
        }

        if (!organizationDashboardData || organizationDashboardData.length === 0) {
            return renderEmptyState();
        }

        // Prepare table data
        const tableData = (organizationDashboardData || []).map((brandData) => {
            if (!brandData) {
                return {
                    id: 0,
                    brandName: 'Unknown Brand',
                    brandImage: null,
                    brandSlug: '',
                    tier: 'FREE',
                    totalFanbase: 0,
                    fanbaseGrowth: 0,
                    error: true
                };
            }

            const fanbaseData = brandData.fanbaseData;
            const weeklyChartData = fanbaseData?.weeklyChartData as FanbaseChartData[] | undefined;
            const currentFanbase = weeklyChartData?.[weeklyChartData.length - 1]?.fanbase_count || 0;
            const growthPercentage = fanbaseData?.weeklyGrowth?.percentage ?? 0;

            return {
                id: brandData.brandId || 0,
                brandName: brandData.brandName || 'Unknown Brand',
                brandImage: brandData.brandImage || null,
                brandSlug: brandData.brandSlug || '',
                tier: brandData.brandTier || 'FREE',
                totalFanbase: currentFanbase,
                fanbaseGrowth: growthPercentage,
                error: brandData.error || false
            };
        });

        return (
            <TableElement
                className="organizationBrandsTable"
                rowData={tableData}
                columnDefs={[
                    {
                        field: 'brandName',
                        title: 'Brand',
                        flex: 2,
                        renderCell: (row, column) => {
                            if (!row) {
                                return <div style={{ color: Colors.greyDark }}>-</div>;
                            }
                            
                            return (
                                <BrandInfo
                                    className="brandInfoOrganizationTable"
                                    titleColor={Colors.black}
                                    brandName={row.brandName || 'Unknown Brand'}
                                    brandImage={row.brandImage || undefined}
                                    brandSlug={row.brandSlug || ''}
                                />
                            );
                        }
                    },
                    {
                        field: 'totalFanbase',
                        title: 'Total Fanbase',
                        flex: 1,
                        renderCell: (row, column) => {
                            if (!row) {
                                return <div style={{ color: Colors.greyDark }}>-</div>;
                            }
                            
                            if (row.error) {
                                return <div style={{ color: Colors.black, fontSize: 14 }}>-</div>;
                            }
                            
                            const growth = row.fanbaseGrowth || 0;
                            
                            return (
                                <div style={{ color: Colors.black, fontSize: 14 }}>
                                    {millify(row.totalFanbase || 0)}{' '}
                                    <span style={{ 
                                        color: growth >= 0 ? Colors.green : Colors.red,
                                        fontSize: 12,
                                        fontWeight: 600
                                    }}>
                                        ({growth >= 0 ? '+' : ''}{growth.toFixed(1)}%)
                                    </span>
                                </div>
                            );
                        }
                    },
                    {
                        field: 'actions',
                        title: '',
                        flex: 3,
                        renderCell: (row, column) => {
                            if (!row) {
                                return <div style={{ color: Colors.greyDark }}>-</div>;
                            }
                            
                            const brand = loadedBrandsDict?.[row.id] as CurrentBrand;
                            const isAdminOrOwner = hasOrganizationAdminAccess();
                            
                            return (
                                <div className="organizationBrandsTableActionsCell" style={sty.tableActionsCell}>
                                    <ButtonSymphony
                                        className="buttonViewDashboardOrganizationBrands"
                                        onClick={() => handleViewBrand(row.id)}
                                        variant="text"
                                        size="small"
                                    >
                                        View Dashboard
                                    </ButtonSymphony>
                                    <BrandActionMenu
                                        className="brandActionMenuOrganizationHomePage"
                                        handleMenuClick={() => setSelectedBrand(row.id)}
                                        isAdminOrOwner={isAdminOrOwner}
                                        onDashboardClick={() => handleViewBrand(row.id)}
                                        canDelete={isAdminOrOwner && (organizationDashboardData?.length || 0) > 1}
                                        openDeleteModal={() => {
                                            setDeleteBrandModalOpen(true);
                                            setSelectedBrand(row.id);
                                        }}
                                        openEditPermissionsModal={() => {
                                            setEditPermissionsModalOpen(true);
                                            setSelectedBrand(row.id);
                                        }}
                                        selectedBrand={brand}
                                    />
                                </div>
                            );
                        }
                    }
                ] as ColDef[]}
                pagination={false}
                rowsTitle="brands"
                isHover={true}
            />
        );
    };

    const renderGraphsTab = () => {
        return (
            <div className="graphsTabContent" style={sty.graphsTabContent}>
                {/* Existing graph functionality can be moved here */}
                <div className="brandsGrid" style={mStyle(sty, 'brandsGrid')}>
                    {organizationDashboardData?.map((brandData) => {
                        return renderBrandCard(brandData);
                    })}
                </div>
            </div>
        );
    };



    const handleAddBrand = () => {
        setShowAddBrandModal(true);
    };

    const handleFinishAddingBrand = () => {
        setShowAddBrandModal(false);
        // Refresh dashboard data to show new brand
        loadDashboardData();
    };

    const handleDeleteBrand = async () => {
        if (!selectedBrand?.id || !organization?.id) {
            return;
        }

        try {
            await deleteOrganizationBrand({
                organizationId: organization.id,
                brandId: selectedBrand.id,
            });
            
            // Close modal and refresh data
            setDeleteBrandModalOpen(false);
            setSelectedBrand(null);
            loadDashboardData();
        } catch (error) {
            console.error('Failed to delete brand from organization:', error);
        }
    };

    const handleCreateCampaign = () => {
        openCreateCampaign({
            source: 'team_dashboard_header'
        });
    };

    const handleCreateWebsite = () => {
        openCreateWebsite({
            source: 'team_dashboard_header'
        });
    };

    const handleProfilePictureUpdate = (newUrl: string) => {
        setCurrentProfilePicture(newUrl);
        // Optionally refresh organization data to get the latest state
        // getOrganization() could be called here if needed
    };

    const handleConnectPlatform = (brandId: number, missingConnections: string[]): void => {
        // For now, just connect the first missing platform
        // In the future, we could show a selection modal
        if (missingConnections.length > 0) {
            const firstMissingPlatform = missingConnections[0];
            const [platformId] = firstMissingPlatform.split('_');

            // Map platform IDs to SocialConnectType
            const platformMap: Record<string, SocialConnectType> = {
                'spotify': SocialConnectType.SPOTIFY,
                'youtube': SocialConnectType.YOUTUBE,
                'instagram': SocialConnectType.INSTAGRAM,
                'facebook': SocialConnectType.FACEBOOK,
                'tiktok': SocialConnectType.TIKTOK,
                'soundcloud': SocialConnectType.SOUNDCLOUD,
                'audiomack': SocialConnectType.AUDIOMACK
            };

            const socialConnectType = platformMap[platformId];
            if (socialConnectType) {
                // Set the brand context for connection
                const brand = organizationDashboardData?.find(b => b.brandId === brandId);
                if (brand) {
                    const brandForConnection = {
                        id: brand.brandId,
                        name: brand.brandName,
                        slug: brand.brandSlug,
                        tier: brand.brandTier,
                    };

                    // Set local state for connection modal
                    setConnectionBrandForModal(brandForConnection as any);
                    // Temporarily set currentBrand for SocialConnectModal to work
                    setCurrentBrand(brandForConnection as any);
                    setSelectedPlatformForConnect(socialConnectType);
                }
            }
        }
    };

    const handleConnectionComplete = (): void => {
        setShowConnectionModal(false);
        setConnectionBrandId(null);
        setConnectionPlatformType(null);

        // Refresh dashboard data to get updated connections
        loadDashboardData();
    };

    const handleExpandGraph = (brandData: OrganizationDashboardBrandData, platformKey?: string): void => {
        setShowLargeGraph({
            shown: true,
            brandData,
            platformKey
        });
    };

    const renderConnectionsModals = () => {
        if (selectedPlatformForConnect === SocialConnectType.FACEBOOK) {
            return (
                <AdConnectionModal
                    subtitle="Connect your Facebook Page to track followers."
                    isOpen={!!selectedPlatformForConnect}
                    onClose={() => {
                        setSelectedPlatformForConnect(null);
                        setConnectionBrandForModal(null);
                    }}
                    connectionType={AdConnectionType.FACEBOOK_PAGE}
                    brandId={connectionBrandForModal?.id}
                />
            )
        }
        return (
            <>
                {selectedPlatformForConnect && connectionBrandForModal &&
                    <SocialConnectModal
                        className="socialConnectModalOrganizationPage"
                        isOpen={!!selectedPlatformForConnect}
                        onClose={() => {
                            setSelectedPlatformForConnect(null);
                            setConnectionBrandForModal(null);
                        }}
                        socialConnectType={selectedPlatformForConnect}
                        validateBusinessAccount
                        onConnect={() => {
                            // Refresh data after connection
                            loadDashboardData();
                            setSelectedPlatformForConnect(null);
                            setConnectionBrandForModal(null);
                        }}
                    />
                }
            </>
        )
    };

    const renderProfilePicture = () => {
        return (
            <ProfilePictureSection
                profilePictureUrl={currentProfilePicture}
                organizationId={organization?.id}
                size={40}
                isClickable={true}
                showUploadButton={false}
                onProfilePictureUpdate={handleProfilePictureUpdate}
                placeholder={
                    <div style={sty.teamAvatarPlaceholder}>
                        <Icon
                            className="teamAvatarHomeIcon"
                            icon="home"
                            size={Math.floor(40 * 0.5)}
                            color={Colors.white}
                        />
                    </div>
                }
            />
        );
    };

    const renderActionButtons = () => {
        if (mobileView) {
            return undefined; // Hide on mobile to avoid crowding
        }

        return (
            <div className="teamDashboardActions" style={sty.teamDashboardActions}>
                <ButtonSymphony
                    className="addBrandButton"
                    variant="primary"
                    iconLeft="plus"
                    iconLeftSize={16}
                    onClick={handleAddBrand}
                >
                    Add Brand
                </ButtonSymphony>
                <ButtonSymphony
                    className="createCampaignButton"
                    variant="primary"
                    iconLeft="plus"
                    iconLeftSize={16}
                    onClick={handleCreateCampaign}
                >
                    Create Campaign
                </ButtonSymphony>
                <ButtonSymphony
                    className="createWebsiteButton"
                    variant="primary"
                    iconLeft="plus"
                    iconLeftSize={16}
                    onClick={handleCreateWebsite}
                >
                    Create Website
                </ButtonSymphony>
            </div>
        );
    };

    return (
        <PageSymphony
            className="organizationHomePage"
            isContent
        >


            <div className="mainPageLayout" style={mStyle(sty, 'mainPageLayout')}>
                <div className="leftContentSection" style={mStyle(sty, 'leftContentSection')}>
                    <CardSymphony
                        className="cardTitleTeamDashboard"
                        title={
                            <div className="teamDashboardTitle" style={sty.teamDashboardTitle}>
                                {renderProfilePicture()}
                                {organization?.name || 'Team Dashboard'}
                            </div>
                        }
                        subtitle={`${organizationDashboardData?.length || 0} Brands`}
                        additionalButtons={renderActionButtons()}
                    >
                        {/* Future: Add learning indicator or additional actions here */}
                    </CardSymphony>

                    <CardSymphony
                        className="organizationHomeTabsContainer"
                    >
                        <div
                            className="organizationHomeTabsNavBar"
                            style={sty.tabsNavBar}
                        >
                            <TabsSymphony
                                className="organizationHomeTabsSelector"
                                selectedTab={selectedTab}
                                onChange={(tab) => setSelectedTab(tab as TabItem)}
                                tabs={ORGANIZATION_HOME_TABS}
                                isBorderBottom
                                isLoading={isLoadingOrganizationDashboard}
                            />
                        </div>

                        <TabSymphony
                            className="organizationHomeBrandsTab"
                            visible={selectedTab.id === OrganizationHomeTabs.BRANDS}
                        >
                            {renderBrandsTab()}
                        </TabSymphony>

                        <TabSymphony
                            className="organizationHomeGraphsTab"
                            visible={selectedTab.id === OrganizationHomeTabs.GRAPHS}
                        >
                            {renderGraphsTab()}
                        </TabSymphony>
                    </CardSymphony>

                    {mobileView && organizationCampaigns.length > 0 && (
                        <div style={{ marginTop: 24 }}>
                            <CampaignsSidebar
                                className="organizationCampaignsSidebar"
                                campaigns={adaptCampaignsForSidebar(organizationCampaigns)}
                                isLoading={isLoadingCampaigns}
                                title="Recent Campaigns"
                                showBrandInfo={true}
                                showRecommendations={true}
                            />
                        </div>
                    )}
                </div>

                {!mobileView && (
                    <div className="rightSidebarSection" style={mStyle(sty, 'rightSidebarSection')}>
                        <CampaignsSidebar
                            className="organizationCampaignsSidebar"
                            campaigns={adaptCampaignsForSidebar(organizationCampaigns)}
                            isLoading={isLoadingCampaigns}
                            title="Recent Campaigns"
                            showBrandInfo={true}
                            showRecommendations={true}
                        />
                    </div>
                )}
            </div>

            {/* Connection Modal */}
            {showConnectionModal && connectionPlatformType && (
                <SocialConnectModal
                    className="organizationConnectionModal"
                    isOpen={showConnectionModal}
                    onClose={() => setShowConnectionModal(false)}
                    onConnect={handleConnectionComplete}
                    socialConnectType={connectionPlatformType}
                    validateBusinessAccount={true}
                    isConnectToBrand={true}
                />
            )}

            {/* Platform Connection Modals */}
            {renderConnectionsModals()}

            {/* Edit Connections Modal */}
            <PlatformsConnector
                isOpen={showEditConnectionsModal}
                closeModal={() => {
                    setShowEditConnectionsModal(false);
                    // Clear connection brand context
                    setConnectionBrandForModal(null);
                    // Refresh data after editing connections
                    loadDashboardData();
                }}
                brand={connectionBrandForModal || undefined}
                onPlatformSelect={(platformType: SocialConnectType) => {
                    setSelectedPlatformForConnect(platformType);
                    setShowEditConnectionsModal(false);
                }}
            />

            {/* Large Graph Popup */}
            {showLargeGraph.shown && showLargeGraph.brandData && (
                <ModalSymphony
                    className="organizationLargeGraphPopup"
                    isOpen={showLargeGraph.shown}
                    onClose={() => setShowLargeGraph({ shown: false, brandData: null })}
                    maxWidth={900}
                >
                    <div style={{ padding: 24 }}>
                        <h2 style={{ color: Colors.white, marginBottom: 16 }}>
                            {showLargeGraph.brandData.brandName} - Fanbase Growth
                        </h2>
                        <div style={{
                            backgroundColor: Colors.greyDark + '20',
                            borderRadius: 8,
                            padding: 16,
                            marginBottom: 16
                        }}>
                            <p style={{ color: Colors.greyDark, margin: 0 }}>
                                Enhanced graph view with platform filtering will be available here.
                                This would include the same platform toggle functionality and
                                detailed analytics for the selected brand.
                            </p>
                        </div>
                        <ButtonSymphony
                            className="closeLargeGraphButton"
                            variant="primary"
                            onClick={() => setShowLargeGraph({ shown: false, brandData: null })}
                        >
                            Close
                        </ButtonSymphony>
                    </div>
                </ModalSymphony>
            )}

            {/* Add Brand Modal */}
            <AddBrandToOrganizationModalV2
                className="organizationAddBrandModal"
                isOpen={showAddBrandModal}
                onClose={() => setShowAddBrandModal(false)}
                onFinishAddingToOrganization={handleFinishAddingBrand}
            />

            {/* Delete Brand Confirmation Modal */}
            <DeleteBrandConfirmationModal
                isOpen={deleteBrandModalOpen}
                onClose={() => setDeleteBrandModalOpen(false)}
                onDeleteAction={handleDeleteBrand}
            />

            {/* Edit Brand User Permissions Modal */}
            <EditBrandUserPermissionsModal
                isOpen={editPermissionsModalOpen}
                onClose={() => {
                    setEditPermissionsModalOpen(false);
                    setSelectedBrand(null);
                }}
            />
        </PageSymphony>
    );
};

const sty: Record<string, CSSProperties> = {
    pageContent: {
        flex: 1
    },
    brandsGrid: {
        display: 'grid',
        gridTemplateColumns: 'repeat(2, 1fr)',
        gap: 24
    },
    brandsGrid_medium: {
        gridTemplateColumns: '1fr'
    },
    brandCard: {
        backgroundColor: Colors.white,
        borderRadius: 16,
        padding: 24,
        border: `1px solid ${Colors.greyLight}`
    },

    brandCardContent: {
        minHeight: 200
    },
    fanbaseStats: {
        display: 'flex',
        gap: 32,
        marginBottom: 20
    },
    statLabel: {
        fontSize: 12,
        color: Colors.greyDark,
        marginBottom: 4,
        textTransform: 'uppercase'
    },
    statValue: {
        fontSize: 24,
        fontWeight: 600,
        color: Colors.white
    },
    graphContainer: {
        marginTop: 20
    },
    loadingContainer: {
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        minHeight: 200,
        gap: 16
    },
    loadingText: {
        fontSize: 14,
        color: Colors.greyDark
    },
    errorMessage: {
        color: Colors.red,
        fontSize: 14,
        textAlign: 'center',
        padding: 20
    },
    emptyState: {
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        minHeight: 400,
        gap: 16
    },
    emptyStateTitle: {
        fontSize: 24,
        fontWeight: 600,
        color: Colors.white
    },
    emptyStateText: {
        fontSize: 16,
        color: Colors.greyDark,
        marginBottom: 16
    },
    teamDashboardActions: {
        display: 'flex',
        gap: 8,
        alignItems: 'center'
    },
    teamDashboardTitle: {
        display: 'flex',
        alignItems: 'center',
        gap: 12,
    },
    organizationProfilePlaceholder: {
        width: '100%',
        height: '100%',
        backgroundColor: Colors.purple,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        fontSize: 16,
        fontWeight: 'bold',
        color: Colors.white,
        borderRadius: '50%',
    },
    teamAvatarPlaceholder: {
        width: '100%',
        height: '100%',
        backgroundColor: Colors.purple,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        borderRadius: '50%',
    },
    missingConnections: {
        backgroundColor: Colors.greyDark + '20',
        borderRadius: 8,
        padding: 12,
        marginBottom: 16,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between'
    },
    missingConnectionsText: {
        fontSize: 12,
        color: Colors.greyDark,
        flex: 1
    },
    mobileContent: {
        display: 'flex',
        flexDirection: 'column',
        width: '100%'
    },
    desktopContent: {
        display: 'flex',
        flexDirection: 'row',
        gap: 24,
        width: '100%'
    },
    mainContent: {
        flex: 1,
        minWidth: 0 // Allows flex item to shrink
    },
    sidebarContent: {
        width: 340,
        flexShrink: 0
    },
    platformViewsAndDateContainer: {
        backgroundColor: Colors.white,
        borderRadius: 8,
        padding: 16,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        gap: 24,
        flexWrap: 'wrap'
    },
    platformViewsSection: {
        display: 'flex',
        alignItems: 'center',
        gap: 12,
        flex: 1,
        minWidth: 0
    },
    viewsLabel: {
        fontSize: 14,
        fontWeight: 600,
        color: Colors.black,
        flexShrink: 0
    },
    platformTogglesList: {
        display: 'flex',
        gap: 8,
        flexWrap: 'wrap',
        overflowX: 'auto',
        scrollbarWidth: 'none'
    },
    platformViewToggle: {
        fontSize: 12,
        padding: '6px 12px',
        borderWidth: 1,
        borderStyle: 'solid',
        borderRadius: 20,
        flexShrink: 0,
        transition: 'all 0.2s ease'
    },
    dateSelectorSection: {
        flexShrink: 0
    },
    mainPageLayout: {
        display: 'flex',
        flexDirection: 'row',
        gap: 16,
        maxWidth: 1200,
        width: '100%'
    },
    mainPageLayout_medium: {
        flexDirection: 'column'
    },
    leftContentSection: {
        display: 'flex',
        flexDirection: 'column',
        gap: 24,
        width: 'calc(100% - 356px)'
    },
    leftContentSection_medium: {
        width: '100%'
    },
    rightSidebarSection: {
        position: 'sticky',
        top: 24,
        backgroundColor: Colors.white,
        padding: 16,
        width: 340,
        borderRadius: 8,
        flexShrink: 0,
        alignSelf: 'flex-start',
        overflowY: 'scroll',
        scrollbarWidth: 'none',
        maxHeight: 'calc(100vh - 48px)'
    },
    brandsSection: {
        flex: 1
    },
    brandCardHeaderButtons: {
        display: 'flex',
        alignItems: 'center',
        gap: 8
    },
    brandCardActions: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        gap: 8,
        marginTop: 16,
        paddingTop: 16,
        borderTop: `1px solid ${Colors.greyLight}`
    },
    tabsNavBar: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        marginBottom: 16,
    },
    tableActionsCell: {
        display: 'flex',
        alignItems: 'center',
        gap: 8,
        justifyContent: 'flex-end'
    },
    graphsTabContent: {
        // Placeholder for graphs tab content
    }
};

export default OrganizationHomePage;